/**
 * Panel Docking — drag-and-drop panel rearrangement
 *
 * Three docking modes based on cursor position:
 *   Center 60% of target panel → swap DOM positions
 *   Left/Right 20% of target  → insert source adjacent to target
 *   Far left/right of workspace (40px edge) → full-height dock
 *
 * Program Monitor is always locked. Workspace Focus can also lock
 * the Project List or Shot Launcher panel.
 *
 * Uses custom MIME type `application/x-panel-id` to avoid conflict
 * with the template drag-drop system (`application/x-template-id`).
 */
import { eventBus } from './eventBus';
import { initVerticalResize } from './panelResize';
import { uiStore } from '../stores/uiStore';

const STATIC_LOCKED = new Set(['program']);

/** Check if a panel is locked (static lock or workspace-focus lock) */
function isPanelLocked(panelId: string): boolean {
  if (STATIC_LOCKED.has(panelId)) return true;
  const focus = uiStore.getState().workspaceFocus;
  return focus !== 'none' && focus === panelId;
}
const MIME = 'application/x-panel-id';
const DROP_CLASSES = ['panel-drop-target', 'panel-drop-locked', 'panel-drop-left', 'panel-drop-right'] as const;
const WS_DROP_CLASSES = ['ws-drop-left', 'ws-drop-right'] as const;
const EDGE_THRESHOLD = 40; // px from workspace edge for full-height dock

/** Module-scoped because dataTransfer.getData() is blocked during dragover */
let currentDragPanelId: string | null = null;

/**
 * Track whether the most recent mousedown was on a panel header.
 * We need this because dragstart's e.target is the draggable element
 * (the panel), not the element the mouse is actually over.
 */
let mouseDownOnHeader = false;

// ─── Workspace edge detection ──────────────────────────────────────

function getWorkspaceEdge(clientX: number): 'left' | 'right' | null {
  const workspace = document.getElementById('workspace');
  if (!workspace) return null;
  const rect = workspace.getBoundingClientRect();
  if (clientX < rect.left + EDGE_THRESHOLD) return 'left';
  if (clientX > rect.right - EDGE_THRESHOLD) return 'right';
  return null;
}

// ─── Panel edge detection ──────────────────────────────────────────

type DropZone = 'left' | 'center' | 'right';

function getDropZone(panel: HTMLElement, clientX: number): DropZone {
  const rect = panel.getBoundingClientRect();
  const relX = clientX - rect.left;
  const pct = relX / rect.width;
  if (pct < 0.2) return 'left';
  if (pct > 0.8) return 'right';
  return 'center';
}

// ─── Divider helpers ───────────────────────────────────────────────

function createDivider(): HTMLElement {
  const div = document.createElement('div');
  div.className = 'panel-divider-v';
  initVerticalResize(div);
  return div;
}

function isDivider(el: Element | null): boolean {
  return !!el && el.classList.contains('panel-divider-v');
}

// ─── Row helpers ───────────────────────────────────────────────────

/**
 * Remove a panel from its parent (row or workspace), cleaning up one
 * adjacent divider.
 *
 * Middle:  [prev_div] [PANEL] [next_div]  → remove prev_div + panel
 * First:   [PANEL] [next_div]             → remove panel + next_div
 * Last:    [prev_div] [PANEL]             → remove prev_div + panel
 * Alone:   [PANEL]                        → remove panel only
 */
function removePanelFromParent(panel: HTMLElement): void {
  const parent = panel.parentElement;
  if (!parent) return;

  const prev = panel.previousElementSibling;
  const next = panel.nextElementSibling;

  if (isDivider(prev)) {
    parent.removeChild(prev!);
  } else if (isDivider(next)) {
    parent.removeChild(next!);
  }
  parent.removeChild(panel);
}

/**
 * Set `flex: 1` on every panel in a row so they share space equally.
 * Skips `.graphics-library` which has a fixed 220px width.
 * Clears inline width/height so flex takes over.
 */
function equalizePanelFlex(row: HTMLElement): void {
  row.querySelectorAll<HTMLElement>(':scope > .panel').forEach((p) => {
    if (p.classList.contains('graphics-library')) return;
    p.style.flex = '1';
    p.style.width = '';
    p.style.height = '';
  });
}

// ─── Full-height dock ──────────────────────────────────────────────

function movePanelFullHeight(source: HTMLElement, side: 'left' | 'right'): void {
  const sourceParent = source.parentElement as HTMLElement;
  const workspace = document.getElementById('workspace')!;
  // Full-height panels go into ws-body (if header layout) or workspace directly
  const container = (workspace.querySelector('.ws-body') || workspace) as HTMLElement;
  const wsMain = container.querySelector('.ws-main') as HTMLElement;
  if (!sourceParent || !wsMain) return;

  // No-op if already a full-height panel on the requested side
  if (sourceParent === container) {
    const mainIdx = Array.from(container.children).indexOf(wsMain);
    const srcIdx = Array.from(container.children).indexOf(source);
    if (side === 'left' && srcIdx < mainIdx) return;
    if (side === 'right' && srcIdx > mainIdx) return;
  }

  // 1. Remove from current position
  removePanelFromParent(source);

  // 2. Set full-height sizing (fixed width, resizable via divider)
  source.style.flex = 'none';
  source.style.width = '300px';
  source.style.height = '';

  // 3. Insert into container with a new divider
  const divider = createDivider();

  if (side === 'left') {
    container.insertBefore(source, container.firstChild);
    container.insertBefore(divider, source.nextSibling);
  } else {
    container.appendChild(divider);
    container.appendChild(source);
  }

  // 4. Equalize source row if it was a row (not workspace)
  if (sourceParent.classList.contains('ws-row')) {
    equalizePanelFlex(sourceParent);
  }

  // 5. Notify system
  window.dispatchEvent(new Event('resize'));
  eventBus.emit('ui:panelMoved', {
    panelId: source.dataset.panelId!,
    targetPanelId: '__workspace__',
    side,
  });
}

// ─── Move (insert-next-to) ─────────────────────────────────────────

function movePanel(source: HTMLElement, target: HTMLElement, side: 'left' | 'right'): void {
  const sourceParent = source.parentElement as HTMLElement;
  const targetRow = target.parentElement as HTMLElement;
  if (!sourceParent || !targetRow) return;

  // Adjacent no-op guard — skip if already next to target on requested side
  if (sourceParent === targetRow) {
    if (side === 'left') {
      let prev: Element | null = target.previousElementSibling;
      while (prev && isDivider(prev)) prev = prev.previousElementSibling;
      if (prev === source) return;
    } else {
      let next: Element | null = target.nextElementSibling;
      while (next && isDivider(next)) next = next.nextElementSibling;
      if (next === source) return;
    }
  }

  // 1. Remove source from its current position (with divider cleanup)
  removePanelFromParent(source);

  // 2. Clear source inline sizing so it adopts the new row layout
  source.style.flex = '';
  source.style.width = '';
  source.style.height = '';

  // 3. Insert source + new divider adjacent to target
  const divider = createDivider();

  if (side === 'left') {
    targetRow.insertBefore(source, target);
    targetRow.insertBefore(divider, target);
  } else {
    const ref = target.nextSibling;
    targetRow.insertBefore(divider, ref);
    targetRow.insertBefore(source, ref);
  }

  // 4. Equalize both containers (only actual rows, not workspace)
  if (targetRow.classList.contains('ws-row')) {
    equalizePanelFlex(targetRow);
  }
  if (sourceParent !== targetRow && sourceParent.classList.contains('ws-row')) {
    equalizePanelFlex(sourceParent);
  }

  // 5. Notify system
  window.dispatchEvent(new Event('resize'));
  eventBus.emit('ui:panelMoved', {
    panelId: source.dataset.panelId!,
    targetPanelId: target.dataset.panelId!,
    side,
  });
}

// ─── Swap (existing behavior) ──────────────────────────────────────

/**
 * Swap two panels' DOM positions and transfer inline flex/width styles
 * so each panel adopts the sizing of its new slot.
 */
function swapPanels(a: HTMLElement, b: HTMLElement): void {
  const aFlex = a.style.flex;
  const aWidth = a.style.width;
  const aHeight = a.style.height;
  const bFlex = b.style.flex;
  const bWidth = b.style.width;
  const bHeight = b.style.height;

  const placeholderA = document.createComment('swap-a');
  const placeholderB = document.createComment('swap-b');

  a.parentNode!.insertBefore(placeholderA, a);
  b.parentNode!.insertBefore(placeholderB, b);

  placeholderA.parentNode!.replaceChild(b, placeholderA);
  placeholderB.parentNode!.replaceChild(a, placeholderB);

  a.style.flex = bFlex;
  a.style.width = bWidth;
  a.style.height = bHeight;
  b.style.flex = aFlex;
  b.style.width = aWidth;
  b.style.height = aHeight;

  window.dispatchEvent(new Event('resize'));
  eventBus.emit('ui:panelsSwapped', {
    panelA: a.dataset.panelId!,
    panelB: b.dataset.panelId!,
  });
}

// ─── Indicator class helpers ───────────────────────────────────────

function clearDropClasses(el: HTMLElement): void {
  el.classList.remove(...DROP_CLASSES);
}

function clearAllDropClasses(): void {
  document.querySelectorAll<HTMLElement>(
    DROP_CLASSES.map((c) => `.${c}`).join(','),
  ).forEach(clearDropClasses);

  const workspace = document.getElementById('workspace');
  if (workspace) {
    workspace.classList.remove(...WS_DROP_CLASSES);
  }
}

// ─── Init ──────────────────────────────────────────────────────────

export function initPanelDocking(): void {
  const panels = document.querySelectorAll<HTMLElement>('[data-panel-id]');

  panels.forEach((panel) => {
    const panelId = panel.dataset.panelId!;
    if (isPanelLocked(panelId)) return;

    panel.draggable = true;

    const header = panel.querySelector('.panel-header') as HTMLElement | null;

    // Track mousedown origin — header vs body
    panel.addEventListener('mousedown', (e) => {
      if (header && header.contains(e.target as Node)) {
        const btn = (e.target as HTMLElement).closest('.panel-btn');
        mouseDownOnHeader = !btn;
      } else {
        mouseDownOnHeader = false;
      }
    });

    // --- Drag start: only from panel header ---
    panel.addEventListener('dragstart', (e) => {
      // Don't interfere with child draggable elements (library templates, project rows)
      if (e.target !== panel) return;

      if (!mouseDownOnHeader) {
        e.preventDefault();
        return;
      }

      currentDragPanelId = panelId;
      e.dataTransfer!.setData(MIME, panelId);
      e.dataTransfer!.effectAllowed = 'move';

      requestAnimationFrame(() => panel.classList.add('panel-dragging'));
      document.body.classList.add('panel-docking');
    });

    panel.addEventListener('dragend', () => {
      currentDragPanelId = null;
      mouseDownOnHeader = false;
      panel.classList.remove('panel-dragging');
      document.body.classList.remove('panel-docking');
      clearAllDropClasses();
    });
  });

  // --- Drop target listeners on ALL panels (including locked for visual feedback) ---
  panels.forEach((panel) => {
    const panelId = panel.dataset.panelId!;

    panel.addEventListener('dragover', (e) => {
      if (currentDragPanelId === null) return;
      if (currentDragPanelId === panelId) return;

      e.preventDefault();
      e.dataTransfer!.dropEffect = 'move';

      // Check workspace edges first (full-height docking takes priority)
      const wsEdge = getWorkspaceEdge(e.clientX);
      if (wsEdge) {
        clearDropClasses(panel);
        const workspace = document.getElementById('workspace')!;
        workspace.classList.remove(...WS_DROP_CLASSES);
        workspace.classList.add(wsEdge === 'left' ? 'ws-drop-left' : 'ws-drop-right');
        return;
      }

      // Not near workspace edge — clear workspace indicators
      const workspace = document.getElementById('workspace');
      if (workspace) workspace.classList.remove(...WS_DROP_CLASSES);

      if (isPanelLocked(panelId)) {
        e.dataTransfer!.dropEffect = 'none';
        clearDropClasses(panel);
        panel.classList.add('panel-drop-locked');
        return;
      }

      // Determine panel-level zone and apply indicator
      const zone = getDropZone(panel, e.clientX);
      clearDropClasses(panel);

      if (zone === 'left') {
        panel.classList.add('panel-drop-left');
      } else if (zone === 'right') {
        panel.classList.add('panel-drop-right');
      } else {
        panel.classList.add('panel-drop-target');
      }
    });

    panel.addEventListener('dragleave', (e) => {
      if (!panel.contains(e.relatedTarget as Node)) {
        clearDropClasses(panel);
      }
    });

    panel.addEventListener('drop', (e) => {
      e.preventDefault();
      clearDropClasses(panel);

      const sourceId = e.dataTransfer?.getData(MIME);
      if (!sourceId || sourceId === panelId) return;

      const sourcePanel = document.querySelector<HTMLElement>(
        `[data-panel-id="${sourceId}"]`,
      );
      if (!sourcePanel) return;

      // Check workspace edges first (full-height docking)
      const wsEdge = getWorkspaceEdge(e.clientX);
      if (wsEdge) {
        clearAllDropClasses();
        movePanelFullHeight(sourcePanel, wsEdge);
        return;
      }

      clearAllDropClasses();

      if (isPanelLocked(panelId)) return;

      const zone = getDropZone(panel, e.clientX);

      if (zone === 'center') {
        swapPanels(sourcePanel, panel);
      } else {
        movePanel(sourcePanel, panel, zone);
      }
    });
  });

  // --- Workspace-level listeners for drops on dividers/gaps ---
  const workspace = document.getElementById('workspace');
  if (workspace) {
    workspace.addEventListener('dragover', (e) => {
      if (currentDragPanelId === null) return;
      // Only handle if not already on a panel (panels handle themselves)
      if ((e.target as HTMLElement).closest('[data-panel-id]')) return;

      const wsEdge = getWorkspaceEdge(e.clientX);
      if (wsEdge) {
        e.preventDefault();
        e.dataTransfer!.dropEffect = 'move';
        workspace.classList.remove(...WS_DROP_CLASSES);
        workspace.classList.add(wsEdge === 'left' ? 'ws-drop-left' : 'ws-drop-right');
      } else {
        workspace.classList.remove(...WS_DROP_CLASSES);
      }
    });

    workspace.addEventListener('dragleave', (e) => {
      if (!workspace.contains(e.relatedTarget as Node)) {
        workspace.classList.remove(...WS_DROP_CLASSES);
      }
    });

    workspace.addEventListener('drop', (e) => {
      if (currentDragPanelId === null) return;

      const wsEdge = getWorkspaceEdge(e.clientX);
      if (!wsEdge) return;

      e.preventDefault();
      clearAllDropClasses();

      const sourceId = e.dataTransfer?.getData(MIME);
      if (!sourceId) return;

      const sourcePanel = document.querySelector<HTMLElement>(
        `[data-panel-id="${sourceId}"]`,
      );
      if (!sourcePanel) return;

      movePanelFullHeight(sourcePanel, wsEdge);
    });
  }
}
